import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Input from '../../../components/Input';
import { getEmailFromQueryString, googleLibrary, isValid } from '../../../utils';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthHandlers } from '../../../services/actions/auth/useAuthHandlers';
import { useAuthRequest } from '../../../services/apis/auth/useAuthRequest';
import {
    IInviteAuthContext,
    InviteAuthMethod,
    isInviteLandingResponse,
    isInvitePendingRegistration,
    parseStoredInviteValue,
    getInvitePendingVerification,
    setInvitePendingVerification,
    clearInvitePendingVerification,
} from '../../../interfaces/models/invite';
import { AppRoutes } from '../../../interfaces/store/appStore';
import { styles } from '../../../styles/themes/style';
import SvgFragment from '../../../fragments/SvgFragment';
import {
    AuthPageContainer,
    AuthInnerContainer,
    AuthColumn,
    FullLogo,
    HeaderSection,
    AuthCard,
    AuthMethodButton,
    OrDivider,
    DividerLine,
    OrText,
    AuthCardInlineFooter,
    AuthTitle,
    AuthSubtitle,
    AuthLink,
    AuthFormFields,
    AuthField,
    AuthFieldLabelRow,
    AuthFieldInlineLink,
    AuthInputWrap,
    AuthPasswordInputWrap,
    AuthPasswordFieldWrap,
    AuthPasswordToggle,
    AuthFooterTextSmall,
    PasswordVisibilityIcon,
} from '../authFormCommon';
import CheckInboxCard from '../components/CheckInboxCard';
import AuthLegalFooter from '../components/AuthLegalFooter';
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers';
import {
    clearLastLoginUser,
    getLastLoginUser,
    getLastLoginUserDisplayName,
    getRememberedLoginMethod,
    ILastLoginUser,
    saveLastLoginUser,
} from '../lastLoginUserStorage';

const SignInSection = React.forwardRef((props: any, ref: any) => {
    const [values, setValues] = React.useState<any>({});
    const [valid, setValid] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [invite, setInvite] = React.useState<IInviteAuthContext>();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { setAuthDataHandler } = useAuthHandlers()
    const { signInEmail, signInGoogle, invitePublic, workOSGetAuthorizationUrl, resendVerificationEmail: resendVerificationEmailRequest, checkEmail } = useAuthRequest();
    const { showErrorToast, showSuccessToast } = useAppHandlers();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [verifyEmail, setVerifyEmail] = useState('');
    const [ssoEmail, setSsoEmail] = React.useState('');
    const [ssoValid, setSsoValid] = React.useState(false);
    const [rememberedUser, setRememberedUser] = React.useState<ILastLoginUser | null>(null);
    const [emailCheckError, setEmailCheckError] = React.useState('');
    const [emailCheckedFor, setEmailCheckedFor] = React.useState('');
    const [emailCheckLoading, setEmailCheckLoading] = React.useState(false);
    const [inviteLoginEmail, setInviteLoginEmail] = React.useState<string | null>(null);
    const handledInviteLoginRef = React.useRef(false);
    const handledAlreadyVerifiedRef = React.useRef(false);
    const handledDesktopEmailRef = React.useRef(false);

    /* useEffect(() => {
        resetAuthStore()
    }, []) */

    useEffect(() => {
        document.title = "LeadDelta Login";
      }, []);

    const googleCallback = React.useCallback((...args: any[]) => {
        if (typeof googleLibrary.callback === 'function') googleLibrary.callback(...args);
    }, []);

    const initGoogle = React.useCallback(() => {
        // Google
        if ((window as any).google) {
            // new
            (window as any).google.accounts.id.initialize({
                client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                callback: googleCallback,
                use_fedcm_for_prompt: true
            });

            // oauth2 init token
            googleLibrary.oauth2 = (window as any).google.accounts.oauth2.initTokenClient({
                client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                scope: 'profile email'
            });
        }
    }, []);

    const googlePrompt = React.useCallback(() => {
        if ((window as any).google) {
            googleLibrary.callback = SigninWithGoogle;

            (window as any).google.accounts.id.prompt();
        }
    }, []);

    const cleanUp = React.useCallback(() => {
        window.history.pushState({}, '', window.location.pathname);
    }, []);

    const rememberedName = React.useMemo(
        () => getLastLoginUserDisplayName(rememberedUser),
        [rememberedUser],
    );

    const resetEmailCheckState = React.useCallback(() => {
        setEmailCheckError('');
        setEmailCheckedFor('');
    }, []);

    const clearRememberedUser = React.useCallback(() => {
        clearLastLoginUser();
        setRememberedUser(null);
        setValues((previous: any) => ({
            ...previous,
            email: '',
            password: '',
        }));
        setSsoEmail('');
        setSsoValid(false);
        setValid(false);
        resetEmailCheckState();
        setStep(1);
        googlePrompt();
    }, [resetEmailCheckState, googlePrompt]);

    const rememberedLoginMethod = getRememberedLoginMethod(rememberedUser);
    const isRememberedGoogle = Boolean(rememberedUser && !invite && rememberedLoginMethod === 'google');
    const isRememberedSso = Boolean(rememberedUser && !invite && rememberedLoginMethod === 'sso');
    const isRememberedFlow = Boolean(rememberedUser && !invite);

    const ensureEmailChecked = React.useCallback(async () => {
        const email = values.email?.trim();
        if (!isValid.email(email)) return false;
        if (emailCheckedFor === email) return !emailCheckError;

        setEmailCheckLoading(true);
        try {
            const response = await checkEmail(email);
            const exists = Boolean(response?.data?.exists);
            const nextError = exists ? '' : 'No account found with this email. Please sign up first.';
            setEmailCheckError(nextError);
            setEmailCheckedFor(email);
            return !nextError;
        } finally {
            setEmailCheckLoading(false);
        }
    }, [values.email, emailCheckedFor, emailCheckError, checkEmail]);

    const SigninWithEmail = React.useCallback(async (event?: React.FormEvent) => {
        if (event) event.preventDefault();
        setLoading(true);
        try {
            if (valid) {
                const canProceed = await ensureEmailChecked();
                if (!canProceed) return;
                const response = await signInEmail({
                    ...values,

                    ...(invite?.inviteCode && { inviteCode: invite.inviteCode })
                });

                if (response?.data?.requiresEmailVerification) {
                    setVerifyEmail(response.data.email || values.email);
                    setStep(4);
                    return;
                }

                if (response?.success && response?.data?.user) {
                    clearInvitePendingVerification();
                    saveLastLoginUser(response.data.user, 'email');
                    setAuthDataHandler({
                        accessToken: `Bearer ${response.data.token}`,
                        refreshToken: response.data.refreshToken,
                    });
                    cleanUp();
                } else if (!response?.success) {
                    showErrorToast({ message: response?.message || 'Unable to sign in.' });
                }
            }
        }
        catch (error) {
            console.log('Sign in with email', error);
            showErrorToast({ message: 'Unexpected error occurred.' });
        }
        finally {
            setLoading(false);
        }
    }, [values, invite, valid, cleanUp, setAuthDataHandler, showErrorToast, ensureEmailChecked]);

    const resendVerificationEmail = React.useCallback(async () => {
        if (!verifyEmail) return;
        setLoading(true);
        try {
            const response = await resendVerificationEmailRequest({ email: verifyEmail });

            if (response?.success) {
                showSuccessToast({ message: 'Verification email sent! Please check your inbox.' });
            } else {
                showErrorToast({ message: response?.message || 'Unable to resend verification email.' });
            }
        } catch (error) {
            console.log('Resend verification email', error);
            showErrorToast({ message: 'Unexpected error occurred.' });
        } finally {
            setLoading(false);
        }
    }, [verifyEmail, resendVerificationEmailRequest, showSuccessToast, showErrorToast]);

    const SigninWithGoogle = React.useCallback(async (...args: any[]) => {
        setLoading(true);
        console.log('On sign up with Google', ...args);

        const googleResponse = args[0];

        const valid_ = googleResponse.access_token || googleResponse.credential;

        const inviteValueObj = localStorage.getItem('inviteValue')

        try {
            if (valid_) {
                const body: any = {};

                const storedInvite = parseStoredInviteValue(inviteValueObj);
                if (storedInvite?.inviteCode) {
                    body['inviteCode'] = storedInvite.inviteCode;
                    localStorage.removeItem('inviteValue');
                }

                if (googleResponse.access_token) body['access_token'] = googleResponse.access_token;

                if (googleResponse.credential) body['credential'] = googleResponse.credential;

                const response = await signInGoogle(body);

                if (response?.user) {
                    saveLastLoginUser(response.user, 'google');
                    setAuthDataHandler({ accessToken: `Bearer ${response.token}`, refreshToken: response.refreshToken });

                    //showSuccessToast({ message: `You signed in successfully!` });

                    // clean up 
                    cleanUp();

                    // Auto open the extension 
                    // if all the checks are good 
                    //navigate(AppRoutes.PROCESSING)
                }
                else {
                    //showErrorToast({ message: response?.message });
                }
            }
        }
        catch (error) {
            console.log('Sign up with Google', error);
        }

        setLoading(false);
    }, [invite, valid]);

    const init = React.useCallback(async () => {
        // Init Google 
        initGoogle();

        const inviteCode = searchParams.get('invite');

        if (inviteCode) {
            const landing = await invitePublic(inviteCode);

            if (isInviteLandingResponse(landing) && isInvitePendingRegistration(landing)) {
                const inviteContext = { inviteCode, landing };
                const pendingVerification = getInvitePendingVerification();

                if (pendingVerification?.inviteCode === inviteCode) {
                    localStorage.setItem('inviteValue', JSON.stringify(inviteContext));
                    navigate(`${AppRoutes.SIGNUP}?invite=${inviteCode}`);
                    return;
                }

                const invitedEmail = landing.invite?.email?.trim();
                if (invitedEmail) {
                    try {
                        const emailCheck = await checkEmail(invitedEmail);
                        if (emailCheck?.data?.exists) {
                            setInvitePendingVerification({
                                inviteCode,
                                email: invitedEmail,
                            });
                            localStorage.setItem('inviteValue', JSON.stringify(inviteContext));
                            setInvite(inviteContext);
                            setInviteLoginEmail(invitedEmail);
                            setValues({ email: invitedEmail, password: '' });
                            setVerifyEmail(invitedEmail);
                            setStep(4);
                            return;
                        }
                    } catch {
                        /* fall through to signup */
                    }
                }

                localStorage.setItem('inviteValue', JSON.stringify(inviteContext));
                navigate(`${AppRoutes.SIGNUP}?invite=${inviteCode}`);
                return;
            }
        }

        const hasInvite = Boolean(searchParams.get('invite'));
        const hasRememberedUser = !hasInvite && Boolean(getLastLoginUser());
        if (!hasRememberedUser) {
            googlePrompt();
        }
    }, [checkEmail, invitePublic, navigate, searchParams]);

    React.useEffect(() => {
        init();
    }, []);

    React.useEffect(() => {
        const signInEntryState = location.state as {
            fromInviteLogin?: boolean;
            fromAlreadyVerified?: boolean;
            authMethod?: InviteAuthMethod | null;
            inviteEmail?: string | null;
        } | null;

        if (signInEntryState?.fromAlreadyVerified && !handledAlreadyVerifiedRef.current) {
            handledAlreadyVerifiedRef.current = true;
            setRememberedUser(null);
            resetEmailCheckState();
            setInviteLoginEmail(null);
            setValues({ email: '', password: '' });
            setValid(false);
            setSsoEmail('');
            setSsoValid(false);
            setStep(1);
            navigate(location.pathname, { replace: true, state: null });
            return;
        }

        if (signInEntryState?.fromInviteLogin && !handledInviteLoginRef.current) {
            handledInviteLoginRef.current = true;
            setRememberedUser(null);
            resetEmailCheckState();

            const invitedEmail = signInEntryState.inviteEmail?.trim() ?? '';
            if (invitedEmail) {
                setInviteLoginEmail(invitedEmail);
                setValues({ email: invitedEmail, password: '' });
                setValid(false);
                setSsoEmail(invitedEmail);
                setSsoValid(!!isValid.email(invitedEmail) || invitedEmail.includes('.'));
            } else {
                setInviteLoginEmail(null);
                setValues({ email: '', password: '' });
                setValid(false);
            }

            if (signInEntryState.authMethod === 'email') {
                setStep(2);
            } else if (signInEntryState.authMethod === 'workos') {
                setStep(3);
            } else {
                setStep(1);
            }

            navigate(location.pathname, { replace: true, state: null });
            return;
        }

        if (handledInviteLoginRef.current || handledAlreadyVerifiedRef.current) return;

        if (searchParams.get('invite')) return;

        const isDesktopEmailLink = searchParams.get('ref') === 'desktop-email';
        const desktopEmail = isDesktopEmailLink ? getEmailFromQueryString(location.search) : null;
        if (
            isDesktopEmailLink &&
            desktopEmail &&
            isValid.email(desktopEmail) &&
            !handledDesktopEmailRef.current
        ) {
            handledDesktopEmailRef.current = true;
            setRememberedUser(null);
            resetEmailCheckState();
            setInviteLoginEmail(null);
            setValues({ email: desktopEmail, password: '' });
            setValid(false);
            setSsoEmail(desktopEmail);
            setSsoValid(true);
            setStep(2);

            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('email');
            setSearchParams(nextParams, { replace: true });
            return;
        }

        if (handledDesktopEmailRef.current) return;

        const stored = getLastLoginUser();
        if (!stored) return;
        setRememberedUser(stored);
        const method = getRememberedLoginMethod(stored);
        if (method === 'sso') {
            setSsoEmail(stored.email);
            setSsoValid(true);
            setStep(3);
            return;
        }
        setValues((previous: any) => ({
            ...previous,
            email: stored.email,
        }));
        setStep(2);
    }, [searchParams, location.state, location.pathname, navigate, resetEmailCheckState, setSearchParams]);

    const getGoogleAccessToken = () => {
        return new Promise((resolve, reject) => {
            if (googleLibrary.oauth2) {
                googleLibrary.oauth2.callback = (response: any) => resolve(response);

                googleLibrary.oauth2.error_callback = (response: any) => resolve(response);

                googleLibrary.oauth2.requestAccessToken();
            }
        });
    }

    const onGoogleSignInClick = React.useCallback(async () => {
        const response = await getGoogleAccessToken();

        // Use SigninWithGoogle method with response as an argument
        SigninWithGoogle(response);
    }, []);

    const updateValid = React.useCallback((valuesNew: any) => {
        const validNew = !!(
            isValid.email(valuesNew.email) &&
            valuesNew.password?.length > 7
        );

        setValid(validNew);
    }, []);

    const updateValue = React.useCallback((property: string, valueNew: any) => {
        setValues((previous: any) => {
            const valuesNew = {
                ...previous,
                [property]: valueNew
            };
            if (property === 'email') resetEmailCheckState();

            // Valid 
            updateValid(valuesNew);

            return valuesNew;
        });
    }, [resetEmailCheckState]);

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter' && valid) {
            event.preventDefault();
            SigninWithEmail();
        }
    };

    const updateSsoEmail = React.useCallback((value: string) => {
        setSsoEmail(value);
        setSsoValid(!!isValid.email(value) || value.includes('.'));
    }, []);

    const SigninWithSSO = React.useCallback(async (event?: React.FormEvent) => {
        if (event) event.preventDefault();
        setLoading(true);
        try {
            if (ssoValid) {
                const response = await workOSGetAuthorizationUrl({ 
                    email: ssoEmail 
                });

                if (response?.url) {
                    // Save email to sessionStorage for SSO callback
                    sessionStorage.setItem('sso_email', ssoEmail);
                    
                    // Redirect to WorkOS SSO
                    window.location.href = response.url;
                } else {
                    setLoading(false);
                }
            }
        }
        catch (error) {
            console.log('Sign in with SSO error', error);
            setLoading(false);
        }
    }, [ssoEmail, ssoValid]);

    const handleSsoKeyDown = (event: any) => {
        if (event.key === 'Enter' && ssoValid) {
            event.preventDefault();
            SigninWithSSO();
        }
    };

    return (
        <AuthPageContainer>
            {step === 1 ? (
            <AuthInnerContainer>
                <AuthColumn>
                    <FullLogo />
                    <HeaderSection>
                        <AuthTitle>Log in to LeadDelta</AuthTitle>
                    </HeaderSection>
                    <AuthCard>
                        <AuthMethodButton
                            onClickHandler={onGoogleSignInClick}
                            disabled={loading}
                            $type="blue"
                            tooltipMessage=''
                        >
                            <SvgFragment type='google' />
                            Login with Google
                        </AuthMethodButton>
                        <OrDivider>
                            <DividerLine />
                            <OrText>Or</OrText>
                            <DividerLine />
                        </OrDivider>
                        <AuthMethodButton
                            $type='light'
                            tooltipMessage=''
                            onClickHandler={() => setStep(2)}
                        >
                            Log in with Email
                        </AuthMethodButton>
                        <AuthMethodButton
                            $type='light'
                            tooltipMessage=''
                            onClickHandler={() => setStep(3)}
                        >
                            Log in with SSO
                        </AuthMethodButton>
                        <AuthCardInlineFooter>
                            New to LeadDelta?{' '}
                            <AuthLink onClick={() => navigate(AppRoutes.SIGNUP)}>Create an account</AuthLink>
                        </AuthCardInlineFooter>
                    </AuthCard>
                </AuthColumn>
            </AuthInnerContainer>
            ) : step === 2 ? (
            <AuthInnerContainer>
                <form onKeyDown={handleKeyDown}>
                    <AuthColumn>
                        <FullLogo />
                        <HeaderSection>
                            <AuthTitle>
                                {isRememberedFlow
                                    ? rememberedName
                                        ? `Welcome back, ${rememberedName}`
                                        : 'Welcome back'
                                    : 'Log in to LeadDelta'}
                            </AuthTitle>
                        </HeaderSection>
                        <AuthCard>
                            <AuthFormFields>
                                <AuthField>
                                    Email address
                                    <AuthInputWrap>
                                        <Input
                                            placeholder='Enter your email address'
                                            type='email'
                                            value={values.email || ''}
                                            onChangeHandler={valueNew => updateValue('email', valueNew)}
                                            onFocus={resetEmailCheckState}
                                            disabled={!!invite || !!rememberedUser || !!inviteLoginEmail}
                                            $error={!!emailCheckError}
                                            className={emailCheckError ? 'email-error' : undefined}
                                        />
                                    </AuthInputWrap>
                                    {!!emailCheckError && <EmailCheckError>{emailCheckError}</EmailCheckError>}
                                </AuthField>
                                {!isRememberedGoogle && (
                                    <AuthField>
                                        <AuthFieldLabelRow>
                                            <span>Password</span>
                                            <AuthFieldInlineLink onClick={() => navigate(AppRoutes.FORGOT_PASSWORD)}>
                                                Forgot password?
                                            </AuthFieldInlineLink>
                                        </AuthFieldLabelRow>
                                        <AuthPasswordFieldWrap>
                                            <AuthPasswordInputWrap>
                                                <Input
                                                    placeholder='At least 8 characters long'
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={values.password || ''}
                                                    onChangeHandler={valueNew => updateValue('password', valueNew)}
                                                    onFocus={() => void ensureEmailChecked()}
                                                    $border={`1px solid ${styles.colors.black200}`}
                                                />
                                                <AuthPasswordToggle
                                                    type="button"
                                                    onClick={() => setShowPassword(previous => !previous)}
                                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                >
                                                    <PasswordVisibilityIcon visible={showPassword} />
                                                </AuthPasswordToggle>
                                            </AuthPasswordInputWrap>
                                        </AuthPasswordFieldWrap>
                                    </AuthField>
                                )}
                            </AuthFormFields>
                            {isRememberedGoogle ? (
                                <AuthMethodButton
                                    $type='blue'
                                    tooltipMessage=''
                                    disabled={loading}
                                    onlySpinner={loading}
                                    spinnerSvg
                                    onClickHandler={onGoogleSignInClick}
                                >
                                    <SvgFragment type='google' />
                                    Login with Google
                                </AuthMethodButton>
                            ) : (
                                <AuthMethodButton
                                    type='submit'
                                    $type='light'
                                    tooltipMessage=''
                                    disabled={!valid || loading || emailCheckLoading || !!emailCheckError}
                                    onlySpinner={loading}
                                    spinnerSvg
                                    onClickHandler={(event) => SigninWithEmail(event)}
                                >
                                    {isRememberedFlow ? 'Log in' : 'Continue with Email'}
                                </AuthMethodButton>
                            )}
                            {isRememberedFlow ? (
                                <AuthCardInlineFooter>
                                    <AuthLink onClick={clearRememberedUser}>Sign in with a different account</AuthLink>
                                    {' '}or{' '}
                                    <AuthLink onClick={() => navigate(AppRoutes.SIGNUP)}>create a new account</AuthLink>
                                </AuthCardInlineFooter>
                            ) : (
                                <AuthCardInlineFooter>
                                    New to LeadDelta?{' '}
                                    <AuthLink onClick={() => navigate(AppRoutes.SIGNUP)}>Create an account</AuthLink>
                                </AuthCardInlineFooter>
                            )}
                        </AuthCard>
                    </AuthColumn>
                </form>
            </AuthInnerContainer>
            ) : step === 4 ? (
            <AuthInnerContainer>
                <AuthColumn>
                    <FullLogo />
                    <HeaderSection>
                        <AuthTitle>Check your inbox</AuthTitle>
                        <AuthSubtitle>
                            {invite?.landing?.invite?.workspace?.name
                                ? `We sent a verification link to ${verifyEmail}. Once verified, you'll join ${invite.landing.invite.workspace.name}.`
                                : `We sent a verification link to ${verifyEmail}`}
                        </AuthSubtitle>
                    </HeaderSection>
                    <CheckInboxCard
                        loading={loading}
                        onResend={resendVerificationEmail}
                    />
                    <AuthFooterTextSmall>
                        <AuthLink onClick={() => setStep(2)}>Back to login</AuthLink>
                    </AuthFooterTextSmall>
                </AuthColumn>
            </AuthInnerContainer>
            ) : (
            <AuthInnerContainer>
                <form onKeyDown={handleSsoKeyDown}>
                    <AuthColumn>
                        <FullLogo />
                        <HeaderSection>
                            <AuthTitle>
                                {isRememberedSso
                                    ? rememberedName
                                        ? `Welcome back, ${rememberedName}`
                                        : 'Welcome back'
                                    : 'Log in with SSO'}
                            </AuthTitle>
                            {!isRememberedSso && (
                                <AuthSubtitle>
                                    Enter your work email to continue with your company&apos;s Single Sign-On.
                                </AuthSubtitle>
                            )}
                        </HeaderSection>
                        <AuthCard>
                            <AuthFormFields>
                                <AuthField>
                                    Work email address
                                    <AuthInputWrap>
                                        <Input
                                            placeholder='you@company.com'
                                            type='email'
                                            value={ssoEmail}
                                            onChangeHandler={valueNew => updateSsoEmail(valueNew)}
                                            disabled={isRememberedSso || !!inviteLoginEmail}
                                        />
                                    </AuthInputWrap>
                                </AuthField>
                            </AuthFormFields>
                            <AuthMethodButton
                                type='submit'
                                $type='light'
                                tooltipMessage=''
                                disabled={!ssoValid || loading}
                                onlySpinner={loading}
                                spinnerSvg
                                onClickHandler={(event) => SigninWithSSO(event)}
                            >
                                Continue with SSO
                            </AuthMethodButton>
                            {isRememberedSso ? (
                                <AuthCardInlineFooter>
                                    <AuthLink onClick={clearRememberedUser}>Sign in with a different account</AuthLink>
                                </AuthCardInlineFooter>
                            ) : (
                                <AuthCardInlineFooter>
                                    <AuthLink onClick={() => setStep(1)}>Back to login</AuthLink>
                                </AuthCardInlineFooter>
                            )}
                        </AuthCard>
                    </AuthColumn>
                </form>
            </AuthInnerContainer>
            )}
            <AuthLegalFooter />
      </AuthPageContainer>
    );
});

const EmailCheckError = styled.span`
    color: ${styles.colors.red600};
    font-size: 11px;
    font-weight: 400;
    line-height: 20px;
`;

export default SignInSection; 
